import { FormEvent, RefObject, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { fetchContext, homeFor, nextStep } from '../lib/staffContext';
import { AuthFrame } from '../ui/AuthFrame';
import { ContextError } from '../ui/ContextError';
import { useI18n } from '../i18n';

type Factor = { id: string; friendly_name?: string; status: string };
type Enrolment = { factorId: string; qr: string; secret: string };
type Mode = 'loading' | 'verify' | 'manage';

// Two-factor sign-in (authenticator app) for platform admins, and — in
// Customer schools — for school admins, heads of school and counsellors
// (0015; the role can't act until this sign-in has done it).
//  - "verify": two-factor is required and this sign-in hasn't done it yet →
//    ask for the 6-digit code.
//  - "manage": set up the first authenticator, add a backup one, see which
//    are registered. Reachable from the admin sidebar before enforcement is
//    switched on (platform_settings.require_mfa_platform_admin).
// There are no printed recovery codes, so the screen asks for a SECOND
// authenticator as a backup.
export function TwoFactor() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [mode, setMode] = useState<Mode>('loading');
  const [error, setError] = useState<string | null>(null);
  const [factors, setFactors] = useState<Factor[]>([]);
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [codeError, setCodeError] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  // Two-factor is enforced for platform admins (platform_settings) and this
  // sign-in hasn't completed it: there is no way into the admin area until it
  // is done, so the only other way out offered is Sign out.
  const [required, setRequired] = useState(false);
  const codeInput = useRef<HTMLInputElement>(null);
  // Platform admins and school roles see slightly different wording and
  // return to their own home.
  const [platform, setPlatform] = useState(true);
  const [home, setHome] = useState('/admin');

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    const res = await fetchContext();
    if (res.kind === 'signed_out') return navigate('/teacher/login', { replace: true });
    if (res.kind === 'no_access') return navigate('/teacher/login?reason=no_access', { replace: true });
    if (res.kind === 'unavailable') return setError(res.message);
    if (res.ctx.active_role !== 'platform_admin' && !res.ctx.mfa_applies) {
      return navigate(nextStep(res.ctx), { replace: true });
    }
    setRequired(res.ctx.mfa_required);
    setPlatform(res.ctx.active_role === 'platform_admin');
    setHome(homeFor(res.ctx.active_role));

    const { data } = await supabase.auth.mfa.listFactors();
    const verified = (data?.totp ?? []) as Factor[];
    setFactors(verified);
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    setMode(verified.length > 0 && aal?.currentLevel !== 'aal2' ? 'verify' : 'manage');
  }

  async function verifySignIn(e: FormEvent) {
    e.preventDefault();
    if (factors.length === 0) return;
    setBusy(true);
    setCodeError(false);
    // Any registered authenticator may answer; try each until one accepts.
    let ok = false;
    for (const f of factors) {
      const { error: err } = await supabase.auth.mfa.challengeAndVerify({ factorId: f.id, code: code.trim() });
      if (!err) {
        ok = true;
        break;
      }
    }
    setBusy(false);
    if (!ok) {
      setCodeError(true);
      return;
    }
    const res = await fetchContext();
    if (res.kind === 'ok') navigate(nextStep(res.ctx), { replace: true });
  }

  async function startEnrolment() {
    setBusy(true);
    setDone(null);
    // Clear any half-finished enrolment first, so names don't collide.
    const { data } = await supabase.auth.mfa.listFactors();
    for (const f of (data?.all ?? []) as Factor[]) {
      if (f.status !== 'verified') await supabase.auth.mfa.unenroll({ factorId: f.id });
    }
    const name = `Swipe authenticator ${factors.length + 1}`;
    const { data: enrolled, error: err } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: name });
    setBusy(false);
    if (err || !enrolled) {
      setCodeError(true);
      return;
    }
    const qr = enrolled.totp.qr_code.startsWith('data:')
      ? enrolled.totp.qr_code
      : `data:image/svg+xml;utf-8,${encodeURIComponent(enrolled.totp.qr_code)}`;
    setEnrolment({ factorId: enrolled.id, qr, secret: enrolled.totp.secret });
    setCode('');
  }

  async function confirmEnrolment(e: FormEvent) {
    e.preventDefault();
    if (!enrolment) return;
    setBusy(true);
    setCodeError(false);
    const { error: err } = await supabase.auth.mfa.challengeAndVerify({
      factorId: enrolment.factorId,
      code: code.trim(),
    });
    setBusy(false);
    if (err) {
      setCodeError(true);
      return;
    }
    setEnrolment(null);
    setCode('');
    const { data } = await supabase.auth.mfa.listFactors();
    const verified = (data?.totp ?? []) as Factor[];
    setFactors(verified);
    setDone(verified.length === 1 ? t['mfa.firstAdded'] : t['mfa.backupAdded']);
    // This sign-in is now two-factor verified, so enforcement no longer blocks it.
    setRequired(false);
  }

  // Abandon a half-finished set-up: remove the unverified authenticator so
  // nothing is left behind, and return to the overview of this screen.
  async function cancelEnrolment() {
    if (enrolment) await supabase.auth.mfa.unenroll({ factorId: enrolment.factorId });
    setEnrolment(null);
    setCode('');
    setCodeError(false);
  }

  async function signOut() {
    if (enrolment) await supabase.auth.mfa.unenroll({ factorId: enrolment.factorId }).catch(() => undefined);
    await supabase.auth.signOut();
    navigate('/teacher/login');
  }

  if (error) return <ContextError message={error} />;
  if (mode === 'loading') return <main className="page-loading" role="status">{t.loading}</main>;

  if (mode === 'verify') {
    return (
      <AuthFrame title={t['mfa.verifyTitle']} subtitle={t['mfa.verifyIntro']} focusField={codeInput}>
        <form className="form" onSubmit={verifySignIn}>
          <CodeField code={code} setCode={setCode} invalid={codeError} inputRef={codeInput} />
          <button type="submit" className="btn btn-primary btn-lg" disabled={busy || code.trim().length !== 6}>
            {busy ? t['mfa.checking'] : t['mfa.verify']}
          </button>
        </form>
        <p className="text-muted">{platform ? t['mfa.lostDevice'] : t['mfaS.lostDevice']}</p>
        <div className="form-actions">
          {!required && (
            <button type="button" className="btn btn-ghost" onClick={() => navigate(home)}>
              {platform ? t['mfa.back'] : t['mfaS.back']}
            </button>
          )}
          <button type="button" className="link-btn" onClick={signOut}>
            {t.signOut}
          </button>
        </div>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title={t['mfa.manageTitle']} subtitle={platform ? t['mfa.manageIntro'] : t['mfaS.manageIntro']}>
      {required && factors.length === 0 && (
        <p className="notice" role="status">
          {platform ? t['mfa.requiredNotice'] : t['mfaS.requiredNotice']}
        </p>
      )}
      <p>{t['mfa.registered'](factors.length)}</p>
      {factors.length === 1 && !enrolment && <p className="notice">{t['mfa.addBackupHint']}</p>}
      {done && (
        <p className="notice" role="status">
          {done}
        </p>
      )}

      {enrolment ? (
        <form className="form" onSubmit={confirmEnrolment}>
          <ol className="mfa-steps">
            <li>{t['mfa.step1']}</li>
            <li>
              {t['mfa.step2']}
              <img className="mfa-qr" src={enrolment.qr} alt={t['mfa.qrAlt']} width={200} height={200} />
              <span className="mfa-secret">
                {t['mfa.secretLabel']} <code>{enrolment.secret}</code>
              </span>
            </li>
            <li>{t['mfa.step3']}</li>
          </ol>
          <CodeField code={code} setCode={setCode} invalid={codeError} />
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy || code.trim().length !== 6}>
              {busy ? t['mfa.checking'] : t['mfa.confirm']}
            </button>
            <button type="button" className="btn btn-ghost" onClick={cancelEnrolment} disabled={busy}>
              {t['form.cancel']}
            </button>
          </div>
        </form>
      ) : (
        <button type="button" className="btn btn-primary btn-lg" onClick={startEnrolment} disabled={busy}>
          {factors.length === 0 ? t['mfa.setUp'] : t['mfa.addBackup']}
        </button>
      )}

      {!enrolment && (
        <div className="form-actions">
          {!required && (
            <button type="button" className="btn btn-ghost" onClick={() => navigate(home)}>
              {factors.length > 0 && done
                ? platform
                  ? t['mfa.continue']
                  : t['mfaS.continue']
                : platform
                  ? t['mfa.back']
                  : t['mfaS.back']}
            </button>
          )}
          <button type="button" className="link-btn" onClick={signOut}>
            {t.signOut}
          </button>
        </div>
      )}
    </AuthFrame>
  );
}

function CodeField({
  code,
  setCode,
  invalid,
  inputRef,
}: {
  code: string;
  setCode: (v: string) => void;
  invalid: boolean;
  inputRef?: RefObject<HTMLInputElement>;
}) {
  const { t } = useI18n();
  return (
    <>
      <label className="field-label" htmlFor="mfa-code">
        {t['mfa.codeLabel']}
      </label>
      <input
        ref={inputRef}
        id="mfa-code"
        className="field field-lg"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={invalid ? 'mfa-error' : undefined}
        required
      />
      {invalid && (
        <p className="error-text" id="mfa-error" role="alert">
          {t['mfa.codeError']}
        </p>
      )}
    </>
  );
}
